import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '@/components/ui/dialog';
import { 
  ArrowLeft, 
  Trash2, 
  UserPlus, 
  Shield, 
  Users,
  Mail,
  UserX,
  UserCheck,
  Loader2,
  Settings,
  Check,
  X,
  Info,
  Database,
  RefreshCw
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_cc23a1c1-e998-45d0-adb8-2e9c1c544201/artifacts/g7bc3tyb_image.png";

// ISO 27001 Password Requirements Validation
const PASSWORD_REQUIREMENTS = [
  { id: 'length', label: 'At least 8 characters', regex: /.{8,}/ },
  { id: 'uppercase', label: 'One uppercase letter (A-Z)', regex: /[A-Z]/ },
  { id: 'lowercase', label: 'One lowercase letter (a-z)', regex: /[a-z]/ },
  { id: 'digit', label: 'One digit (0-9)', regex: /\d/ },
  { id: 'special', label: 'One special character (!@#$%^&*)', regex: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?]/ }
];

const validatePassword = (password) => {
  return PASSWORD_REQUIREMENTS.every(req => req.regex.test(password));
};

const PasswordRequirements = ({ password }) => (
  <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
    <p className="text-xs font-medium text-slate-600 mb-2 flex items-center gap-1">
      <Info className="w-3 h-3" />
      ISO 27001 Password Requirements
    </p>
    <div className="space-y-1">
      {PASSWORD_REQUIREMENTS.map(req => {
        const isValid = req.regex.test(password);
        return (
          <div key={req.id} className="flex items-center gap-2 text-xs">
            {isValid ? (
              <Check className="w-3 h-3 text-green-500" />
            ) : (
              <X className="w-3 h-3 text-slate-300" />
            )}
            <span className={isValid ? 'text-green-700' : 'text-slate-500'}>{req.label}</span>
          </div>
        );
      })}
    </div>
  </div>
);

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updatingEmail, setUpdatingEmail] = useState(false);
  const [newUser, setNewUser] = useState({
    email: '',
    full_name: '',
    password: '',
    role: 'user'
  });
  const [emailUpdate, setEmailUpdate] = useState({
    new_email: '',
    password: ''
  });
  const [backfilling, setBackfilling] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  // Role descriptions
  const ROLES = [
    { value: 'admin', label: 'Admin', description: 'Full access to all folders, teams, and user management' },
    { value: 'lead', label: 'Lead', description: 'Can create teams/folders, manage team members' },
    { value: 'user', label: 'User', description: 'Access to assigned teams only' }
  ];

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`);
      setUsers(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Admin access required');
        navigate('/dashboard');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const createUser = async () => {
    if (!newUser.email || !newUser.full_name || !newUser.password) {
      toast.error('Please fill in all fields');
      return;
    }

    if (!validatePassword(newUser.password)) {
      toast.error('Password does not meet ISO 27001 requirements');
      return;
    }

    setCreating(true);
    try {
      await axios.post(`${API}/admin/users`, newUser);
      toast.success('User created successfully');
      setShowCreateDialog(false);
      setNewUser({ email: '', full_name: '', password: '', role: 'user' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreating(false);
    }
  };

  const updateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/role`, { role: newRole });
      toast.success(`User role updated to ${newRole}`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update role');
    }
  };

  const revokeUser = async (userId) => {
    if (!window.confirm('Are you sure you want to revoke this user\'s access?')) return;

    try {
      await axios.put(`${API}/admin/users/${userId}/revoke`);
      toast.success('User access revoked');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to revoke user');
    }
  };

  const restoreUser = async (userId) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/restore`);
      toast.success('User access restored');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to restore user');
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to permanently delete this user? This action cannot be undone.')) return;

    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const updateAdminEmail = async () => {
    if (!emailUpdate.new_email || !emailUpdate.password) {
      toast.error('Please fill in all fields');
      return;
    }

    setUpdatingEmail(true);
    try {
      await axios.put(`${API}/admin/update-email`, emailUpdate);
      toast.success('Email updated successfully. Please login again with your new email.');
      setShowEmailDialog(false);
      setEmailUpdate({ new_email: '', password: '' });
      // Logout user as they need to login with new email
      localStorage.removeItem('token');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update email');
    } finally {
      setUpdatingEmail(false);
    }
  };

  const runBackfill = async () => {
    setBackfilling(true);
    setBackfillResult(null);
    try {
      const response = await axios.post(`${API}/admin/backfill-voiceover`);
      setBackfillResult(response.data);
      toast.success(`Backfill complete: ${response.data.updated} projects updated`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to run backfill');
    } finally {
      setBackfilling(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              data-testid="back-to-dashboard-btn"
              variant="ghost"
              onClick={() => navigate('/dashboard')}
              className="hover:bg-slate-100"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Dashboard
            </Button>
            <div className="flex items-center gap-3">
              <img src={LOGO_URL} alt="FlowSnap" className="h-9" />
              <div className="h-6 w-px bg-slate-300"></div>
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-red-600" />
                <span className="font-semibold text-slate-900">Admin</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Dialog open={showEmailDialog} onOpenChange={setShowEmailDialog}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="update-email-btn"
                  variant="outline"
                  className="border-slate-300"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  Update Email
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Update Admin Email</DialogTitle>
                  <DialogDescription>
                    Change your admin login email address. You will need to login again after updating.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">New Email</label>
                    <Input
                      data-testid="update-email-input"
                      type="email"
                      placeholder="newemail@example.com"
                      value={emailUpdate.new_email}
                      onChange={(e) => setEmailUpdate({...emailUpdate, new_email: e.target.value})}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-slate-700 mb-2">Current Password</label>
                    <Input
                      data-testid="update-email-password-input"
                      type="password"
                      placeholder="Enter your password to confirm"
                      value={emailUpdate.password}
                      onChange={(e) => setEmailUpdate({...emailUpdate, password: e.target.value})}
                    />
                  </div>
                  <div className="flex gap-2 justify-end pt-4">
                    <Button
                      variant="outline"
                      onClick={() => setShowEmailDialog(false)}
                    >
                      Cancel
                    </Button>
                    <Button
                      data-testid="update-email-submit-btn"
                      onClick={updateAdminEmail}
                      disabled={updatingEmail}
                      className="bg-primary text-white hover:bg-blue-700"
                    >
                      {updatingEmail ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Updating...
                        </>
                      ) : (
                        'Update Email'
                      )}
                    </Button>
                  </div>
                </div>
              </DialogContent>
            </Dialog>
            <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
              <DialogTrigger asChild>
                <Button 
                  data-testid="create-user-btn"
                  className="bg-primary text-white hover:bg-blue-700"
                >
                  <UserPlus className="w-4 h-4 mr-2" />
                  Create User
                </Button>
              </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New User</DialogTitle>
                <DialogDescription>
                  Create a new user account. The user will be able to login immediately.
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Full Name</label>
                  <Input
                    data-testid="new-user-name-input"
                    placeholder="John Doe"
                    value={newUser.full_name}
                    onChange={(e) => setNewUser({...newUser, full_name: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Email</label>
                  <Input
                    data-testid="new-user-email-input"
                    type="email"
                    placeholder="user@example.com"
                    value={newUser.email}
                    onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Password</label>
                  <Input
                    data-testid="new-user-password-input"
                    type="password"
                    placeholder="Create a strong password"
                    value={newUser.password}
                    onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                  />
                  <PasswordRequirements password={newUser.password} />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Role</label>
                  <select
                    data-testid="new-user-role-select"
                    className="w-full border border-slate-300 rounded-md px-3 py-2"
                    value={newUser.role}
                    onChange={(e) => setNewUser({...newUser, role: e.target.value})}
                  >
                    {ROLES.map(role => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  <p className="text-xs text-slate-500 mt-1">
                    {ROLES.find(r => r.value === newUser.role)?.description}
                  </p>
                </div>
                <div className="flex gap-2 justify-end pt-4">
                  <Button
                    variant="outline"
                    onClick={() => setShowCreateDialog(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    data-testid="create-user-submit-btn"
                    onClick={createUser}
                    disabled={creating}
                    className="bg-primary text-white hover:bg-blue-700"
                  >
                    {creating ? (
                      <>
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                        Creating...
                      </>
                    ) : (
                      'Create User'
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-2">User Management</h2>
          <p className="text-slate-600">Create, manage, and control user access to the platform</p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <Card className="p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-blue-50 flex items-center justify-center">
                <Users className="w-6 h-6 text-primary" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{users.length}</p>
                <p className="text-sm text-slate-600">Total Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-green-50 flex items-center justify-center">
                <UserCheck className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {users.filter(u => u.is_approved).length}
                </p>
                <p className="text-sm text-slate-600">Active Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-purple-50 flex items-center justify-center">
                <Shield className="w-6 h-6 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {users.filter(u => u.role === 'lead').length}
                </p>
                <p className="text-sm text-slate-600">Lead Users</p>
              </div>
            </div>
          </Card>
          <Card className="p-6 border border-slate-200">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-red-50 flex items-center justify-center">
                <UserX className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">
                  {users.filter(u => !u.is_approved).length}
                </p>
                <p className="text-sm text-slate-600">Revoked Users</p>
              </div>
            </div>
          </Card>
        </div>

        {/* System Tools */}
        <Card className="p-6 border border-slate-200 mb-8">
          <h3 className="text-lg font-semibold text-slate-900 mb-1">System Tools</h3>
          <p className="text-sm text-slate-500 mb-4">Administrative operations for data maintenance</p>
          <div className="flex items-center gap-4">
            <Button
              data-testid="backfill-voiceover-btn"
              variant="outline"
              onClick={runBackfill}
              disabled={backfilling}
              className="border-slate-300 hover:border-blue-500 hover:bg-blue-50"
            >
              {backfilling ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Running Backfill...
                </>
              ) : (
                <>
                  <Database className="w-4 h-4 mr-2" />
                  Backfill Voiceover Text
                </>
              )}
            </Button>
            {backfillResult && (
              <span className="text-sm text-slate-600">
                {backfillResult.updated} updated, {backfillResult.skipped} already OK (of {backfillResult.total} total)
              </span>
            )}
          </div>
        </Card>

        {/* Users List */}
        <Card className="border border-slate-200 overflow-hidden">
          <div className="p-6 border-b border-slate-200 bg-slate-50">
            <h3 className="text-lg font-semibold text-slate-900">All Users</h3>
          </div>
          <div className="divide-y divide-slate-200">
            {users.map((user, idx) => (
              <motion.div
                key={user.id}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: idx * 0.05 }}
                className="p-6 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${
                      user.role === 'admin' ? 'bg-red-100' : user.role === 'lead' ? 'bg-purple-100' : 'bg-blue-100'
                    }`}>
                      {user.role === 'admin' ? (
                        <Shield className="w-6 h-6 text-red-600" />
                      ) : user.role === 'lead' ? (
                        <Shield className="w-6 h-6 text-purple-600" />
                      ) : (
                        <Mail className="w-6 h-6 text-primary" />
                      )}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-slate-900">{user.full_name}</h4>
                        <Badge 
                          variant={user.role === 'admin' ? 'destructive' : user.role === 'lead' ? 'default' : 'secondary'}
                          className={`text-xs ${
                            user.role === 'admin' ? '' : 
                            user.role === 'lead' ? 'bg-purple-100 text-purple-700' : 
                            'bg-slate-100 text-slate-600'
                          }`}
                        >
                          {user.role === 'admin' ? 'Admin' : user.role === 'lead' ? 'Lead' : 'User'}
                        </Badge>
                        {!user.is_approved && (
                          <Badge variant="secondary" className="text-xs bg-orange-100 text-orange-700">Revoked</Badge>
                        )}
                      </div>
                      <p className="text-sm text-slate-600">{user.email}</p>
                    </div>
                  </div>
                  
                  {user.email !== 'raghuforever@gmail.com' && (
                    <div className="flex items-center gap-2">
                      {/* Role Selector */}
                      <select
                        className="text-sm border border-slate-200 rounded px-2 py-1"
                        value={user.role}
                        onChange={(e) => updateUserRole(user.id, e.target.value)}
                      >
                        <option value="user">User</option>
                        <option value="lead">Lead</option>
                        <option value="admin">Admin</option>
                      </select>
                      
                      {user.is_approved ? (
                        <Button
                          data-testid={`revoke-user-${user.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => revokeUser(user.id)}
                          className="text-orange-600 border-orange-200 hover:bg-orange-50"
                        >
                          <UserX className="w-4 h-4 mr-1" />
                          Revoke
                        </Button>
                      ) : (
                        <Button
                          data-testid={`restore-user-${user.id}`}
                          variant="outline"
                          size="sm"
                          onClick={() => restoreUser(user.id)}
                          className="text-green-600 border-green-200 hover:bg-green-50"
                        >
                          <UserCheck className="w-4 h-4 mr-1" />
                          Restore
                        </Button>
                      )}
                      <Button
                        data-testid={`delete-user-${user.id}`}
                        variant="outline"
                        size="sm"
                        onClick={() => deleteUser(user.id)}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </Card>
      </main>
    </div>
  );
};

export default AdminDashboard;
